import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtIDR, fmtDate } from "@/lib/format";
import { Search, Crown, Wallet, Users, Sparkles, Heart, Cake, Clock, Plus, Pencil, History, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PatientFormDialog from "@/components/PatientFormDialog";
import VisitHistoryDialog from "@/components/VisitHistoryDialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SEGMENTS = [
  { id: "all", label: "Total Database", icon: Users, accent: "bg-slate-50 text-slate-700" },
  { id: "top_spender", label: "Top Spender", icon: Crown, accent: "bg-amber-50 text-amber-700" },
  { id: "deposit_active", label: "Deposit Aktif", icon: Wallet, accent: "bg-emerald-50 text-emerald-700" },
  { id: "deposit_empty", label: "Deposit Habis", icon: Wallet, accent: "bg-rose-50 text-rose-700" },
  { id: "referral", label: "Referral", icon: Heart, accent: "bg-pink-50 text-pink-700" },
  { id: "lifetime", label: "Member", icon: Sparkles, accent: "bg-indigo-50 text-indigo-700" },
  { id: "new", label: "Pasien Baru (30 hari)", icon: Users, accent: "bg-sky-50 text-sky-700" },
  { id: "existing", label: "Pasien Existing", icon: Users, accent: "bg-slate-50 text-slate-700" },
  { id: "post_visit", label: "Post Visit (14 hari)", icon: Clock, accent: "bg-teal-50 text-teal-700" },
  { id: "active_3_12", label: "Aktif 3-12 Bulan", icon: Clock, accent: "bg-emerald-50 text-emerald-700" },
  { id: "inactive_3", label: "Non-aktif >3 Bulan", icon: Clock, accent: "bg-amber-50 text-amber-700" },
  { id: "inactive_6", label: "Non-aktif >6 Bulan", icon: Clock, accent: "bg-orange-50 text-orange-700" },
  { id: "inactive_12", label: "Non-aktif >12 Bulan", icon: Clock, accent: "bg-rose-50 text-rose-700" },
  { id: "birthday_month", label: "Birthday Bulan Ini", icon: Cake, accent: "bg-pink-50 text-pink-700" },
];

export default function Patients() {
  const { branchId, branches } = useBranch();
  const { user } = useAuth();
  const canEdit = ["owner", "super_admin", "manager", "fo_cs"].includes(user?.role);
  const [segment, setSegment] = useState("all");
  const [counts, setCounts] = useState({});
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const canDelete = ["owner", "super_admin", "manager", "fo_cs"].includes(user?.role);
  const confirmDelete = async () => {
    const p = deleting;
    setDeleting(null);
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success(`Data pasien ${p.name} dihapus`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menghapus pasien", { duration: 8000 });
    }
  };

  const refresh = () => {
    const params = { branch_id: branchId };
    if (segment !== "all") params.segment = segment;
    if (search) params.search = search;
    api.get("/patients", { params }).then((r) => setPatients(r.data));
    api
      .get("/patients/segments", { params: { branch_id: branchId } })
      .then((r) => setCounts(r.data));
  };

  useEffect(() => {
    api
      .get("/patients/segments", { params: { branch_id: branchId } })
      .then((r) => setCounts(r.data));
  }, [branchId]);

  useEffect(() => {
    setLoading(true);
    const params = { branch_id: branchId };
    if (segment !== "all") params.segment = segment;
    if (search) params.search = search;
    api
      .get("/patients", { params })
      .then((r) => setPatients(r.data))
      .finally(() => setLoading(false));
  }, [branchId, segment, search]);

  const total = counts.total || 0;

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Database Ekosistem Pasien</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {total.toLocaleString("id-ID")} pasien terdata. Jelajahi segmen untuk eksekusi targeted.
          </p>
        </div>
        <Button
          data-testid="add-patient-btn"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Input Pasien Eksisting
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {SEGMENTS.map((s) => {
          const Icon = s.icon;
          const c = s.id === "all" ? total : counts[s.id] ?? 0;
          const active = segment === s.id;
          return (
            <button
              key={s.id}
              data-testid={`segment-${s.id}`}
              onClick={() => setSegment(s.id)}
              className={cn(
                "rounded-lg border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm",
                active
                  ? "border-foreground bg-card ring-1 ring-foreground"
                  : "border-border bg-card"
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={cn("rounded-md p-1.5", s.accent)}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="font-heading text-2xl tracking-tight">
                  {c.toLocaleString("id-ID")}
                </span>
              </div>
              <div className="mt-2 text-xs font-medium text-foreground">{s.label}</div>
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="font-heading text-lg">
            {SEGMENTS.find((x) => x.id === segment)?.label}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {patients.length} pasien
            </span>
          </CardTitle>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cari nama / no HP / email / No. RM..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 md:w-[300px]"
              data-testid="patients-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pasien</TableHead>
                <TableHead>No. RM</TableHead>
                <TableHead>Kontak</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right">Total Spend</TableHead>
                <TableHead className="text-right">Saldo (Deposit+Cashback)</TableHead>
                <TableHead className="text-right">Visit</TableHead>
                <TableHead>Last Visit</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients.slice(0, 200).map((p) => (
                <TableRow key={p.id} data-testid={`patient-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      {p.avatar_url ? (
                        <img src={p.avatar_url} className="h-8 w-8 rounded-full object-cover" alt="" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-secondary" />
                      )}
                      <div>
                        <div className="text-sm font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.gender === "M" ? "Pria" : "Wanita"} • {p.source}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.medical_record_no ? <span className="font-mono font-medium text-foreground" data-testid={`patient-rm-${p.id}`}>{p.medical_record_no}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{p.phone}</div>
                    <div className="text-muted-foreground">{p.email}</div>
                  </TableCell>
                  <TableCell>
                    {p.member_tier ? (
                      <div className="space-y-0.5">
                        <Badge variant="outline" className="text-xs" data-testid={`patient-member-${p.id}`}>
                          {p.member_tier}
                        </Badge>
                        {p.member_expired_at && (
                          <div className="text-[10px] text-muted-foreground">exp {p.member_expired_at}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    {fmtIDR(p.total_spend)}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    <div className="font-medium" data-testid={`patient-balance-${p.id}`}>{fmtIDR((Number(p.deposit_balance) || 0) + (Number(p.bonus_balance) || 0))}</div>
                    <div className="text-[10px] text-muted-foreground">
                      Deposit {fmtIDR(p.deposit_balance || 0)} · Cashback {fmtIDR(p.bonus_balance || 0)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm">{p.visit_count}</TableCell>
                  <TableCell className="text-xs">{fmtDate(p.last_visit_at)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).map((t) => (
                        <Badge key={t} variant="secondary" className="text-[10px] capitalize">
                          {t.replace("_", " ")}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`history-patient-${p.id}`}
                        onClick={() => setHistoryPatient(p)}
                        title="Riwayat Kunjungan"
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-patient-${p.id}`}
                          onClick={() => {
                            setEditing(p);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          data-testid={`delete-patient-${p.id}`}
                          onClick={() => setDeleting(p)}
                          title="Hapus Pasien"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {patients.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-10">
                    Tidak ada pasien pada segmen ini.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PatientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        patient={editing}
        branches={branches}
        onSaved={refresh}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-patient-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Data Pasien{deleting ? ` — ${deleting.name}` : ""}</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus data pasien ini? Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-patient-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-patient-confirm" className="bg-rose-600 hover:bg-rose-700" onClick={confirmDelete}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <VisitHistoryDialog
        open={!!historyPatient}
        onOpenChange={(o) => !o && setHistoryPatient(null)}
        patient={historyPatient}
      />
    </div>
  );
}
